/**
 * API service for the Work Items Dashboard page.
 *
 * Exports:
 *  - `resolveWarehouseId` — a PURE function over
 *    `(reduxStationCode, urlStationCode)` that resolves the warehouse id and
 *    records its intent to dispatch side effects as data. Keeping it pure
 *    means it can be unit- and property-tested without mocking the store.
 *    Callers (the page component) compose this into React state/effects and
 *    dispatch the returned effects themselves.
 *  - `fetchWorkItemCounts` / `fetchWorkItems` — thin wrappers around
 *    `apiRequest` that dispatch `showError` on failure and re-throw.
 */

import { store } from "../../store";
import { showError } from "../../store/slices/errorSlice";
import { apiRequest } from "../../services/apiMiddleware";
import {
  API_GATEWAY_PROCESS_NAME,
  WORK_ITEMS_ENDPOINT,
  WORK_ITEM_COUNTS_ENDPOINT,
} from "../../utils/constants";
import {
  GET_WORK_ITEMS_API,
  GET_WORK_ITEM_COUNTS_API,
  logOperationalMetric,
  MetricType,
  RCC_SHIFT_PLAN_WORK_ITEMS_DASHBOARD_PAGE,
} from "../../utils/metric-utils";
import {
  GetWorkItemCountsRequest,
  GetWorkItemCountsResponse,
  GetWorkItemsRequest,
  GetWorkItemsResponse,
} from "./model";

// ---------------------------------------------------------------------------
// Warehouse-id resolver (pure)
// ---------------------------------------------------------------------------

// Effect emitted by resolveWarehouseId that the caller must dispatch. Keeping
// the resolver pure means it records its intent as data rather than
// dispatching itself.
export type ResolverEffect =
  | { type: "setStationCode"; stationCode: string }
  | { type: "showError"; title: string; message: string };

export interface ResolverInputs {
  reduxStationCode: string | null | undefined;
  urlStationCode: string | null | undefined;
}

export interface ResolverOutput {
  warehouseId: string | null;
  effects: ResolverEffect[];
}

// Treat empty string, null, and undefined uniformly as "not provided".
const isNonEmpty = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.length > 0;

/**
 * Resolve the warehouse id:
 *   1. If reduxStationCode is non-empty, use it; no effects.
 *   2. Else if urlStationCode is non-empty, use it and emit
 *      setStationCode(urlStationCode) so the rest of the app picks it up.
 *   3. Else return null and emit showError; caller must NOT issue a request.
 */
export const resolveWarehouseId = (
  inputs: ResolverInputs,
): ResolverOutput => {
  const { reduxStationCode, urlStationCode } = inputs;

  if (isNonEmpty(reduxStationCode)) {
    return { warehouseId: reduxStationCode, effects: [] };
  }

  if (isNonEmpty(urlStationCode)) {
    return {
      warehouseId: urlStationCode,
      effects: [{ type: "setStationCode", stationCode: urlStationCode }],
    };
  }

  return {
    warehouseId: null,
    effects: [
      {
        type: "showError",
        title: "Warehouse ID Required",
        message: "Please select a valid warehouse ID from the dropdown",
      },
    ],
  };
};

// ---------------------------------------------------------------------------
// GetWorkItemCounts
// ---------------------------------------------------------------------------

export const fetchWorkItemCounts = async (
  warehouseId: string,
  startDate: string,
): Promise<GetWorkItemCountsResponse> => {
  const startTime = Date.now();
  try {
    const requestBody: GetWorkItemCountsRequest = {
      warehouseId,
      startDate,
    };

    const response = await apiRequest<GetWorkItemCountsResponse>({
      resourcePath: WORK_ITEM_COUNTS_ENDPOINT,
      httpMethod: "post",
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    });

    logOperationalMetric(
      startTime,
      warehouseId,
      RCC_SHIFT_PLAN_WORK_ITEMS_DASHBOARD_PAGE,
      GET_WORK_ITEM_COUNTS_API,
      MetricType.SUCCESS,
    );

    return response.data as GetWorkItemCountsResponse;
  } catch (error: any) {
    console.error("Error fetching work item counts from API:", error);

    logOperationalMetric(
      startTime,
      warehouseId,
      RCC_SHIFT_PLAN_WORK_ITEMS_DASHBOARD_PAGE,
      GET_WORK_ITEM_COUNTS_API,
      MetricType.FAILED,
      error,
    );

    store.dispatch(
      showError({
        title: "Failed to Load Work Item Counts",
        message:
          "Unable to fetch work item counts. Please try again later.",
      }),
    );

    throw error;
  }
};

// ---------------------------------------------------------------------------
// GetWorkItems
// ---------------------------------------------------------------------------

export const fetchWorkItems = async (
  request: GetWorkItemsRequest,
): Promise<GetWorkItemsResponse> => {
  const startTime = Date.now();
  try {
    const { nextToken, ...rest } = request;
    const requestBody: GetWorkItemsRequest = {
      ...rest,
      ...(nextToken ? { nextToken } : {}),
    };

    const response = await apiRequest<GetWorkItemsResponse>({
      resourcePath: WORK_ITEMS_ENDPOINT,
      httpMethod: "post",
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    });

    logOperationalMetric(
      startTime,
      request.warehouseId,
      RCC_SHIFT_PLAN_WORK_ITEMS_DASHBOARD_PAGE,
      GET_WORK_ITEMS_API,
      MetricType.SUCCESS,
    );

    return response.data as GetWorkItemsResponse;
  } catch (error: any) {
    console.error("Error fetching work items from API:", error);

    logOperationalMetric(
      startTime,
      request.warehouseId,
      RCC_SHIFT_PLAN_WORK_ITEMS_DASHBOARD_PAGE,
      GET_WORK_ITEMS_API,
      MetricType.FAILED,
      error,
    );

    store.dispatch(
      showError({
        title: "Failed to Load Work Items",
        message: "Unable to fetch work items. Please try again later.",
      }),
    );

    throw error;
  }
};

// ---------------------------------------------------------------------------
// Re-export from the detail-page API layer
//
// `UpdateWorkItemState` has two call sites: the detail-page Resolve button
// (via `resolveWorkItem` thunk in `workItemDetailSlice`) and the list-page
// `StartContinueTaskButton`. The canonical implementation lives in
// `pages/workItemDetail/api.ts`; this re-export gives the list-page button
// a local import without duplicating the fetcher.
// ---------------------------------------------------------------------------

export { fetchUpdateWorkItemState } from "../workItemDetail/api";
